import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import type { WorkbookSheet } from "./excel";
import { applyReportReferencePresentation } from "./reportReferencePresentation";
const widthNumbers = [0, 1, 2].map(maximumFractionDigits => new Intl.NumberFormat("en-US", { maximumFractionDigits }));

export type ReportWorkbookKind = "store-performance" | "campaign";
type FormulaPlan = Map<string, string>;
const xml = (v: string) => v.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
const col = (n: number) => String.fromCharCode(64 + n);
const kind = (s: WorkbookSheet, i: number) => s.rowKinds?.[i] ?? (i === 0 ? "header" : "detail");
const divide = (a: string, b: string, empty = '""') => `IF(${b}=0,${empty},${a}/${b})`;
const delta = (a: string, b: string) => `IF(${b}=0,"",(${a}-${b})/ABS(${b}))`;

// Sum only detail blocks. Never add both a brand subtotal and its children.
function sum(column: number, indices: number[], offset: number) {
  const blocks: string[] = [];
  for (let i = 0; i < indices.length; i++) {
    const first = indices[i]; let last = first;
    while (indices[i + 1] === last + 1) last = indices[++i];
    blocks.push(first === last ? `${col(column)}${first + offset}` : `${col(column)}${first + offset}:${col(column)}${last + offset}`);
  }
  return blocks.length ? `SUM(${blocks.join(",")})` : "0";
}

function totalChildren(sheet: WorkbookSheet, index: number) {
  if (kind(sheet, index) === "total") return sheet.rows.flatMap((_, i) => kind(sheet, i) === "detail" ? [i] : []);
  let first = index - 1;
  while (first > 0 && kind(sheet, first) === "detail") first--;
  return Array.from({ length: index - first - 1 }, (_, i) => first + i + 1);
}

export function reportFormulaPlans(sheets: WorkbookSheet[], report: ReportWorkbookKind): FormulaPlan[] {
  const widths = report === "campaign" ? [11, 11, 11, 11, 2] : [11, 22, 11, 2];
  if (sheets.length !== widths.length || sheets.some((sheet, i) => sheet.rows[0]?.length !== widths[i])) {
    throw new Error("Report Excel schema changed; formula columns must be reviewed before export.");
  }
  return sheets.map((sheet, sheetIndex) => {
    const formulas: FormulaPlan = new Map();
    if (sheet.theme === "info") return formulas;
    const offset = report === "campaign" ? 2 : 1;
    const put = (c: number, i: number, f: string) => formulas.set(`${col(c)}${i + offset}`, f);
    sheet.rows.forEach((_, i) => {
      const rowKind = kind(sheet, i);
      if (rowKind === "header") return;
      const r = i + offset;
      const total = rowKind === "total" || rowKind === "subtotal";
      if (report === "campaign") {
        if (!total) return; // Retain verified detail data, including matched returns.
        const children = totalChildren(sheet, i);
        for (const c of [3, 4, 6, 7, 9, 10]) {
          // Campaign rows repeat the period denominator; it is not additive.
          const denominator = sheetIndex === 1 && [3, 6, 9].includes(c);
          put(c, i, denominator ? `'${sheets[0].name.replaceAll("'", "''")}'!${col(c)}3` : sum(c, children, offset));
        }
        for (const [a, b, result] of [[4, 3, 5], [7, 6, 8], [10, 9, 11]]) put(result, i, divide(`${col(a)}${r}`, `${col(b)}${r}`));
        return;
      }
      if (sheetIndex === 1 || sheetIndex === 2) {
        const pairs = sheetIndex === 1 ? [[4, 3, 5], [7, 6, 8], [10, 9, 11], [13, 12, 14], [16, 15, 17], [19, 18, 20]] : [[4, 3, 5], [7, 6, 8]];
        pairs.forEach(([a, b, result]) => put(result, i, delta(`${col(a)}${r}`, `${col(b)}${r}`)));
      }
      if (!total) return;
      const children = totalChildren(sheet, i);
      const additive = sheetIndex === 0 ? [3, 4, 5, 6, 7] : sheetIndex === 1 ? [3, 4, 6, 7, 9, 10, 21] : [3, 4, 9, 10];
      additive.forEach(c => put(c, i, sum(c, children, offset)));
      const ratios = sheetIndex === 0 ? [[6, 5, 8, true], [7, 6, 9, false], [4, 6, 10, false], [4, 7, 11, false]]
        : sheetIndex === 1 ? [[9, 6, 12, false], [10, 7, 13, false], [3, 6, 15, false], [4, 7, 16, false], [3, 9, 18, false], [4, 10, 19, false], [7, 21, 22, true]]
        : [[10, 9, 11, true]];
      ratios.forEach(([a, b, result, blank]) => put(Number(result), i, divide(`${col(Number(a))}${r}`, `${col(Number(b))}${r}`, blank ? '""' : "0")));
      if (sheetIndex === 2) {
        // UPT is weighted by receipts, never SUM/AVERAGE of store UPTs.
        const comparison = sheets[1];
        const match = comparison.rows.findIndex((row, j) => kind(comparison, j) === rowKind && row[0] === sheet.rows[i][0] && row[1] === sheet.rows[i][1]);
        if (match < 0) throw new Error("Excel summary subtotal has no matching VS LY row.");
        const name = `'${comparison.name.replaceAll("'", "''")}'!`;
        put(6, i, `${name}L${match + 1}`);
        put(7, i, `${name}M${match + 1}`);
      }
    });
    return formulas;
  });
}

// This opt-in export presentation never touches API calculations or other exports.
export function formatReportWorkbook(bytes: Uint8Array, sheets: WorkbookSheet[], report: ReportWorkbookKind) {
  const files = unzipSync(bytes);
  const formulas = reportFormulaPlans(sheets, report);
  let styles = strFromU8(files["xl/styles.xml"]).replace(/\bx:/g, "").replace('xmlns:x=', 'xmlns=');
  const fontBlock = /<fonts\b[^>]*>([\s\S]*?)<\/fonts>/.exec(styles)!;
  const fonts = [...fontBlock[1].matchAll(/<font\b[^>]*>[\s\S]*?<\/font>/g)].map(m => m[0]
    .replace(/<name val="[^"]*"\s*\/>/, '<name val="Aptos Narrow"/>').replace(/<sz val="[^"]*"\s*\/>/, '<sz val="11"/>'));
  const xfsBlock = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(styles)!;
  const xfs = [...xfsBlock[1].matchAll(/<xf\b[^>]*?\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g)].map(m => m[0]);
  const originalStyleCount = xfs.length;
  const variants = new Map<string, number>();
  const styled = (id: number, alignment: string, header: boolean) => {
    const key = `${id}:${alignment}:${header}`;
    const existing = variants.get(key); if (existing !== undefined) return existing;
    let xf = xfs[id];
    if (!xf) throw new Error(`Unknown Excel style ${id}`);
    if (header) {
      const fontId = Number(/fontId="(\d+)"/.exec(xf)![1]);
      const headerFont = fonts[fontId].replace('<sz val="11"/>', '<sz val="12"/>');
      let newFontId = fonts.indexOf(headerFont);
      if (newFontId < 0) { newFontId = fonts.length; fonts.push(headerFont); }
      xf = xf.replace(/fontId="\d+"/, `fontId="${newFontId}"`);
    }
    xf = xf.replace(/<alignment\b[^>]*\/>/, "");
    if (xf.endsWith("/>")) xf = xf.slice(0, -2) + "></xf>";
    xf = xf.replace(/\sapplyAlignment="[^"]*"/, "").replace('<xf ', '<xf applyAlignment="1" ')
      .replace('</xf>', `<alignment horizontal="${alignment}" vertical="center" wrapText="0"/></xf>`);
    const next = xfs.length; xfs.push(xf); variants.set(key, next); return next;
  };
  sheets.forEach((sheet, i) => {
    const file = `xl/worksheets/sheet${i + 1}.xml`;
    let source = strFromU8(files[file]);
    const info = sheet.theme === "info";
    const offset = report === "campaign" && !info ? 2 : sheet.title || sheet.subtitle ? 4 : 1;
    const money = info ? [] : report === "campaign" ? [9, 10] : i === 0 ? [3, 4, 10, 11] : i === 1 ? [3, 4, 15, 16, 18, 19] : [3, 4];
    source = source.replace(/<row\b[^>]*>/g, row => row.replace(/ht="[^"]*"/, 'ht="17"'))
      .replace(/defaultRowHeight="[^"]*"/, 'defaultRowHeight="17"');
    source = source.replace(/<c\b[^>]*>[\s\S]*?<\/c>/g, cell => {
      const ref = /\br="([A-Z]+)(\d+)"/.exec(cell)!;
      const column = [...ref[1]].reduce((a, c) => a * 26 + c.charCodeAt(0) - 64, 0);
      const r = Number(ref[2]), index = r - offset;
      const header = report === "campaign" && !info ? r <= 2 : r === offset || (offset === 4 && r === 1);
      const numeric = !cell.includes('t="inlineStr"') || (index > 0 && [...(sheet.integers ?? []), ...(sheet.decimals ?? []), ...(sheet.percentages ?? [])].includes(column));
      const align = header || info || !numeric ? "left" : money.includes(column) ? "right" : "center";
      cell = cell.replace(/\bs="(\d+)"/, (_, id) => `s="${styled(Number(id), align, header)}"`);
      const formula = formulas[i].get(`${ref[1]}${r}`);
      if (!formula) return cell;
      const cached = /<v>([^<]*)<\/v>/.exec(cell)?.[1];
      const opening = cell.slice(0, cell.indexOf(">") + 1).replace(/ t="[^"]*"/, "");
      return `${opening.slice(0, -1)}${cached === undefined ? ' t="str"' : ""}><f>${xml(formula)}</f><v>${cached ?? ""}</v></c>`;
    });
    // Fixed 17pt rows require one-line headings/names. Grow widths instead of
    // wrapping, shrinking the requested font or changing the underlying text.
    source = source.replace(/<col\b[^>]*\/>/g, column => {
      const c = Number(/min="(\d+)"/.exec(column)![1]) - 1;
      const longest = Math.max(...sheet.rows.map(row => {
        const value = row[c];
        return typeof value === "number" ? widthNumbers[sheet.percentages?.includes(c + 1) ? 1 : sheet.decimals?.includes(c + 1) ? 2 : 0].format(value).length : String(value ?? "").length;
      }));
      const old = Number(/width="([^"]+)"/.exec(column)![1]);
      // Native Excel needs extra room for bold, grouped UZS grand totals.
      // Limit this to the five Store Performance sales columns; other exports
      // and every value/formula/style stay unchanged.
      const salesWidth = report === "store-performance" && (i === 0 ? c === 3 : (i === 1 || i === 2) && (c === 2 || c === 3)) ? 22 : 0;
      const width = Math.min(255, Math.max(old, longest * (info ? .72 : .8) + 3, salesWidth));
      return column.replace(/width="[^"]*"/, `width="${width.toFixed(2)}"`);
    });
    files[file] = strToU8(source);
  });
  if (xfs.length === originalStyleCount) throw new Error("Report formatting did not apply.");
  styles = styles.replace(fontBlock[0], `<fonts count="${fonts.length}">${fonts.join("")}</fonts>`)
    .replace(xfsBlock[0], `<cellXfs count="${xfs.length}">${xfs.join("")}</cellXfs>`);
  files["xl/styles.xml"] = strToU8(styles);
  files["xl/workbook.xml"] = strToU8(strFromU8(files["xl/workbook.xml"]).replace(/<calcPr\b[^>]*\/>/, '<calcPr calcId="191029" calcMode="auto" fullCalcOnLoad="1" forceFullCalc="1"/>'));
  return applyReportReferencePresentation(zipSync(files, { level: 6 }), sheets, report);
}
